import { Schedule } from "@aura-frontend/omnibus-az-client";
import { useDebounce } from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { useComplexes } from "@/hooks/useComplexes";
import { FieldEventSource } from "@/types/game";
import { getMonthOptions } from "@/utils/dateUtils";
import { getScheduleEventName } from "@/utils/scheduleName";

import { useScheduledEventsForMonth } from "../Complexes/SingleField/FieldSchedule/FieldSchedule.queries";

const TIMEFRAME_MONTH_COUNT = 6;

export const ALL_COMPLEXES = "__all__";

export type EventScheduleRow = {
  id: string;
  name: string;
  startTime: Date;
  fieldName: string;
  complexName: string;
  machineUuid: string;
  /** The field's live game, so a started event can link to it */
  currentGameUuid?: string | null;

  source: FieldEventSource;
  raw: Schedule;
};

type FieldMeta = {
  fieldName: string;
  complexName: string;
  currentGameUuid?: string | null;
};

export const useEventSchedulePage = () => {
  const { complexes, isLoading: isLoadingComplexes } = useComplexes();

  const [selectedComplex, setSelectedComplex] = useState<string>(ALL_COMPLEXES);
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);
  const [selectedMonth, setSelectedMonth] = useState<string>("0");

  const monthOptions = useMemo(
    () => getMonthOptions(TIMEFRAME_MONTH_COUNT),
    [],
  );

  const complexOptions = useMemo(() => {
    const base = [{ value: ALL_COMPLEXES, label: "All" }];
    const items = (complexes ?? []).map((c) => ({
      value: c.name,
      label: c.name,
    }));
    return [...base, ...items];
  }, [complexes]);

  const { fieldIds, fieldMetaByUuid } = useMemo(() => {
    const allComplexes = complexes ?? [];
    const scoped =
      selectedComplex === ALL_COMPLEXES
        ? allComplexes
        : allComplexes.filter((c) => c.name === selectedComplex);

    const ids: string[] = [];
    const meta = new Map<string, FieldMeta>();

    for (const c of scoped) {
      for (const f of c.fields) {
        ids.push(f.machineUuid);
        meta.set(f.machineUuid, {
          fieldName: f.name ?? f.machineUuid,
          complexName: c.name,
          currentGameUuid: f.currentGame?.gameUuid,
        });
      }
    }

    return { fieldIds: ids, fieldMetaByUuid: meta };
  }, [complexes, selectedComplex]);

  const { events, isInitialLoading } = useScheduledEventsForMonth({
    fieldIds,
    monthOffset: Number(selectedMonth),
    fromNow: true,
  });

  const rows: EventScheduleRow[] = useMemo(() => {
    return events.map((schedule) => {
      const machineUuid = schedule.machine_uuid;
      const meta = fieldMetaByUuid.get(machineUuid);

      return {
        id: schedule.id ?? `${machineUuid}-${schedule.schedule_time_s}`,
        name: getScheduleEventName(schedule),
        startTime: new Date(schedule.schedule_time_s * 1000),
        fieldName: meta?.fieldName ?? machineUuid,
        complexName: meta?.complexName ?? "",
        machineUuid,
        currentGameUuid: meta?.currentGameUuid,
        source: (schedule.external?.source as FieldEventSource) || undefined,
        raw: schedule,
      };
    });
  }, [events, fieldMetaByUuid]);

  const filteredRows = useMemo(() => {
    const trimmed = searchValue.trim().toLowerCase();
    if (!trimmed) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(trimmed));
  }, [rows, searchValue]);

  return {
    complexOptions,
    selectedComplex,
    setSelectedComplex,
    monthOptions,
    selectedMonth,
    setSelectedMonth,
    rawSearchValue,
    setSearchValue,
    rows: filteredRows,
    isLoading: isLoadingComplexes || isInitialLoading,
  };
};
